import { useQuery } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";
import { Alert, Box, Button, Switch, Typography } from "@mui/material";
import { useSnackbar } from "notistack";
import { ReactElement } from "react";
import { useForm } from "react-hook-form";
import z from "zod";

import {
  DataBrokerServiceType,
  GetDataBrokerOptInStateQuery,
  GetDataBrokerOptInStateQueryVariables,
  ListSubaccountOwnerFieldsQuery,
  SetDataToWinSubaccountOptInsMutation,
  SetDataToWinSubaccountOptInsMutationVariables,
} from "~frontend/__generated__/graphql";
import { AsyncWrapper } from "~frontend/components/AsyncWrapper";
import { FlexBox } from "~frontend/components/FlexBox";
import Table from "~frontend/components/Table";
import { RhfSwitchField } from "~frontend/components/forms/react-hook-form-components";
import {
  GET_DATA_BROKER_OPT_IN_STATE,
  SET_DATA_TO_WIN_SUBACCOUNT_OPT_INS,
} from "~frontend/graphql/dataBroker";
import theme, { palette } from "~frontend/theme";
import { useErrorMessageFormatter } from "~frontend/util/errors";
import useMutation from "~frontend/util/useMutation";

const TITLE = "Data to Win Opt-in";
// TODO: final copy from product
const DESCRIPTION =
  "Subaccounts opted in to Data to Win will have their data shared with the Data to Win program for enhanced filtering.";

type Subaccount = ListSubaccountOwnerFieldsQuery["listSubaccounts"][number];

const FormSchema = z.object({
  bulkValue: z.boolean(),
  subaccountValues: z.record(z.string(), z.object({ optedIn: z.boolean() })),
});

type FormValues = z.infer<typeof FormSchema>;

export function DataToWinOptInTable({
  subaccounts,
}: {
  subaccounts: Subaccount[];
}): ReactElement {
  const { loading, error, data } = useQuery<
    GetDataBrokerOptInStateQuery,
    GetDataBrokerOptInStateQueryVariables
  >(GET_DATA_BROKER_OPT_IN_STATE, {
    variables: { input: { service: DataBrokerServiceType.DataToWin } },
  });

  return (
    <AsyncWrapper loading={loading} errors={[error]}>
      {data?.dataBrokerOptInState ? (
        <DataToWinOptInForm
          subaccounts={subaccounts}
          optedInSubaccountIds={data?.dataBrokerOptInState.optedInSubaccountIds}
        />
      ) : (
        <FlexBox
          direction="column"
          gap={2}
          sx={{ margin: theme.spacing(4, 0) }}
        >
          <Typography variant="display0Bold" component="h2">
            {TITLE}
          </Typography>
          <Alert severity="info">
            No active Data to Win connection configured.
          </Alert>
        </FlexBox>
      )}
    </AsyncWrapper>
  );
}

function DataToWinOptInForm({
  subaccounts,
  optedInSubaccountIds,
}: {
  subaccounts: Subaccount[];
  optedInSubaccountIds: string[];
}): ReactElement {
  const snackbar = useSnackbar();
  const formatErrorMessage = useErrorMessageFormatter();

  const [saveOptIns] = useMutation<
    SetDataToWinSubaccountOptInsMutation,
    SetDataToWinSubaccountOptInsMutationVariables
  >(SET_DATA_TO_WIN_SUBACCOUNT_OPT_INS, {
    refetchQueries: [GET_DATA_BROKER_OPT_IN_STATE],
    awaitRefetchQueries: true,
  });

  const optedInSet = new Set(optedInSubaccountIds);

  const {
    control,
    getValues,
    setValue,
    handleSubmit,
    formState,
    register,
    reset,
    watch,
  } = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      bulkValue: false,
      subaccountValues: subaccounts.reduce(
        (acc, subaccount) => {
          acc[subaccount.id] = { optedIn: optedInSet.has(subaccount.id) };
          return acc;
        },
        {} as Record<string, { optedIn: boolean }>
      ),
    },
  });

  function handleBulkChange(checked: boolean) {
    const newValues = structuredClone(getValues("subaccountValues"));
    subaccounts.forEach(subaccount => {
      newValues[subaccount.id] = { optedIn: checked };
    });
    setValue("subaccountValues", newValues, { shouldDirty: true });
  }

  async function onSubmit(values: FormValues) {
    const valuesBySubaccount = Object.entries(values.subaccountValues).map(
      ([subaccountId, v]) => ({ subaccountId, optedIn: v.optedIn })
    );

    const { errors } = await saveOptIns({
      variables: { input: { valuesBySubaccount } },
    });

    if (errors) {
      snackbar.enqueueSnackbar(
        `Failed to save Data to Win opt-ins: ${formatErrorMessage(errors)}`,
        { variant: "error" }
      );
      return;
    }

    reset({ bulkValue: false, subaccountValues: values.subaccountValues });
  }

  const allSubaccountValues = watch("subaccountValues");

  return (
    <FlexBox direction="column" gap={2} sx={{ margin: theme.spacing(4, 0) }}>
      <Typography variant="display0Bold" component="h2">
        {TITLE}
      </Typography>

      <FlexBox direction="column">
        <Typography variant="body1">{DESCRIPTION}</Typography>
      </FlexBox>

      <FlexBox direction="row" alignItems="center" gap={2}>
        <Typography variant="body1">Set for all subaccounts:</Typography>
        <RhfSwitchField
          control={control}
          name="bulkValue"
          label=""
          onChange={event => handleBulkChange(event.target.checked)}
        />
      </FlexBox>

      <Box
        sx={{ backgroundColor: palette.white, padding: theme.spacing(2, 5) }}
      >
        <Table<Subaccount>
          rows={subaccounts}
          columns={[
            {
              key: "name",
              renderValue: row => row.name,
              title: "Subaccount",
            },
            {
              key: "optedIn",
              title: "Opted in",
              renderValue: row => (
                <Switch
                  checked={!!allSubaccountValues[row.id]?.optedIn}
                  color="primary"
                  {...register(`subaccountValues.${row.id}.optedIn`)}
                />
              ),
            },
          ]}
          noFooter
        />
        <FlexBox sx={{ padding: theme.spacing(4, 0) }}>
          <Button
            variant="contained"
            disabled={!formState.isDirty || formState.isSubmitting}
            onClick={handleSubmit(onSubmit)}
          >
            Save
          </Button>
        </FlexBox>
      </Box>
    </FlexBox>
  );
}
